/**
 * Checkout / payment telemetry helpers.
 *
 * The checkout-with-payment flow is a chain of distinct steps that used to be
 * logged under a single, ambiguous code ("initiate-order-failed") for three
 * different failures. That made it impossible to tell from the error dashboard
 * whether a payment expired because the order was never created, the card was
 * never confirmed with Stripe, or the marketplace `confirm-payment` transition
 * failed after the card was already charged.
 *
 * This module gives every step its own stable code and a consistent, PII-free
 * structured payload, and mirrors the key moments into Sentry breadcrumbs and
 * LogRocket events so an expiration can be attributed without replaying it.
 */

import * as log from '../../util/log';
import { trackEvent } from '../../util/logrocket';

// Step identifiers — used both as the Sentry error `code` suffix and the
// LogRocket event name, so keep them short and stable.
export const CHECKOUT_STEP = {
  REQUEST_PAYMENT: 'request-payment',
  CONFIRM_CARD_PAYMENT: 'confirm-card-payment',
  CONFIRM_PAYMENT: 'confirm-payment',
  SEND_MESSAGE: 'send-message',
  SAVE_PAYMENT_METHOD: 'save-payment-method',
  SUBMIT: 'submit',
};

const LR_EVENT = {
  stepOk: 'Checkout Step OK',
  stepFailed: 'Checkout Step Failed',
  expiredSeen: 'Checkout Payment Expired Seen',
};

/**
 * Pull the useful, non-PII fields out of whatever error shape we were handed.
 * Handles three shapes:
 *  - storableError() output ({ type:'error', name, message, status, apiErrors })
 *  - a raw Stripe.js error ({ type, code, decline_code, message, payment_intent })
 *  - a Stripe result wrapper ({ error: <stripe error> })
 *
 * @param {Object} err
 * @returns {Object} flat map of scalar fields, safe to send to telemetry
 */
export const describeCheckoutError = err => {
  if (!err || typeof err !== 'object') {
    return { errMessage: String(err ?? 'unknown') };
  }

  const stripeErr = err.error && typeof err.error === 'object' ? err.error : err;
  const firstApiError =
    (Array.isArray(err.apiErrors) && err.apiErrors[0]) ||
    (Array.isArray(err?.data?.errors) && err.data.errors[0]) ||
    null;

  const out = {
    errName: err.name || stripeErr.name,
    errMessage: err.message || stripeErr.message,
    errStatus: err.status ?? stripeErr.status,
  };

  if (firstApiError) {
    out.apiErrorCode = firstApiError.code;
    out.apiErrorStatus = firstApiError.status;
  }

  // Stripe-specific fields (present only for Stripe.js failures).
  if (stripeErr.type && (stripeErr.code || stripeErr.decline_code)) {
    out.stripeType = stripeErr.type;
    out.stripeCode = stripeErr.code;
    out.stripeDeclineCode = stripeErr.decline_code;
  }
  const pi = stripeErr.payment_intent || err.payment_intent;
  if (pi && pi.status) {
    out.paymentIntentStatus = pi.status;
    out.paymentIntentLastError = pi.last_payment_error?.code || pi.last_payment_error?.message;
  }

  // Drop undefined keys so the telemetry payload stays clean.
  return Object.fromEntries(Object.entries(out).filter(([, v]) => v !== undefined));
};

/**
 * Map a checkout failure to one of the short cause codes understood by
 * savePaymentFailureReason / the TransactionPage "the reason:" line. Returns
 * null when the cause is not one we have specific copy for (the generic honest
 * message is shown in that case).
 *
 * @param {Object} err the thrown/rejected error
 * @param {Object} [opts] { step, cardConfirmed }
 * @returns {'authentication'|'cardNotAccepted'|'declined'|'connection'|'chargedNotConfirmed'|'locked'|null}
 */
export const paymentFailureReasonFromError = (err, opts = {}) => {
  const { step, cardConfirmed } = opts;
  const d = describeCheckoutError(err);

  // Marketplace confirm-payment failed after the card was already confirmed.
  if (cardConfirmed) return 'chargedNotConfirmed';

  // Stripe Radar block (decline_code "blocked") — for this marketplace that is
  // almost always the "block prepaid cards" rule, so point the customer at using
  // a different card type rather than a generic "declined".
  if (d.stripeDeclineCode === 'blocked') {
    return 'cardNotAccepted';
  }

  const authCodes = [
    'payment_intent_authentication_failure',
    'payment_intent_authentication_failure_no_pm',
  ];
  if (authCodes.includes(d.stripeCode) || d.stripeDeclineCode === 'authentication_required') {
    return 'authentication';
  }
  if (d.stripeType === 'card_error' || d.stripeDeclineCode || d.stripeCode === 'card_declined') {
    return 'declined';
  }
  if (d.errName === 'TypeError' || d.stripeType === 'api_connection_error') {
    return 'connection';
  }
  if (d.errStatus === 409 || d.apiErrorStatus === 409) {
    return 'locked';
  }
  if (step === CHECKOUT_STEP.CONFIRM_PAYMENT && typeof d.errStatus === 'number' && d.errStatus >= 500) {
    return 'connection';
  }
  return null;
};

/**
 * Record a successful step boundary (Sentry breadcrumb + LogRocket event).
 * Cheap and non-throwing; call it right after each step resolves.
 *
 * @param {string} step one of CHECKOUT_STEP
 * @param {Object} [context] flat scalar context (listingId, txId, elapsedMs, ...)
 */
export const logCheckoutStep = (step, context = {}) => {
  try {
    log.breadcrumb('checkout', `step ok: ${step}`, context);
    trackEvent(LR_EVENT.stepOk, { step, ...context });
  } catch (e) {
    // no-op — telemetry must never break checkout
  }
};

/**
 * Record a failed step.
 *
 * Always adds a Sentry breadcrumb + a LogRocket event with the decoded error
 * fields. When `capture` is true it also sends a distinct Sentry exception with
 * code `checkout-<step>-failed`; pass `capture: false` at call sites where the
 * underlying duck already captures the exception, so the failure isn't
 * double-counted in the error dashboard.
 *
 * @param {string} step one of CHECKOUT_STEP
 * @param {Object} err the thrown/rejected error
 * @param {Object} [context] flat scalar context
 * @param {Object} [options] { capture?: boolean } — default true
 * @returns {Object} the decoded error fields
 */
export const logCheckoutFailure = (step, err, context = {}, options = {}) => {
  const { capture = true } = options;
  const decoded = describeCheckoutError(err);
  const payload = { step, ...decoded, ...context };
  try {
    if (capture) {
      log.error(err, `checkout-${step}-failed`, payload);
    }
    log.breadcrumb('checkout', `step failed: ${step}`, payload);
    trackEvent(LR_EVENT.stepFailed, payload);
  } catch (e) {
    // no-op
  }
  return decoded;
};

/**
 * Record that a customer was shown the "payment expired" state. Lets us count
 * and segment expirations (by process, by whether the card had been charged)
 * from telemetry instead of session replays.
 *
 * @param {Object} [context] { surface: 'checkout' | 'transaction-page', processName, txId, cardCharged }
 */
export const logPaymentExpiredSeen = (context = {}) => {
  try {
    log.breadcrumb('checkout', 'payment expired seen', context);
    log.message('checkout payment expired seen', 'warning', {
      area: 'checkout',
      ...context,
    });
    trackEvent(LR_EVENT.expiredSeen, context);
  } catch (e) {
    // no-op
  }
};
